import React from 'react'
import {Link as RouteLink} from 'react-router-dom'
import {Flex, Text, useMultiStyleConfig, TextProps, FlexProps, Link, LinkProps} from '@chakra-ui/react'
import {Icon, Tag} from '_uiComponents'
import {IconNameTypes} from '../../../types'

interface HeaderLinkProps {
    href?: string
    id?: string
    iconName?: IconNameTypes
    iconSize?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
    colorMode?: 'light' | 'dark'
    shouldShowText?: boolean
    ariaLabel?: string
    isExternal?: boolean
    isAtTop?: boolean
    itemsCount?: number
    isInSidebar?: boolean
    scrollToTop?: boolean
    onClick?: () => void
    children?: React.ReactNode
}

/**
 * @name HeaderLink
 *
 * @component
 *
 * @description
 * Renders a header link with an optional icon and text.
 *
 * @remarks
 * It can include an absolute-positioned tag to display a number value.
 *
 * @param {HeaderLinkProps} props - The properties object.
 * @param {string} [props.href] - The URL to navigate to when the link is clicked.
 * @param {string} [props.id] - The id of the element.
 * @param {string} [props.iconName] - The name of the icon to display next to the link text.
 * @param {string} [props.iconSize] - The size of the icon.
 * @param {string} [props.colorMode='dark'] - The color mode for the link styles.
 * @param {string} props.shouldShowText - Boolean to determine if the text should be displayed.
 * @param {string} [props.ariaLabel] - Aria label for when the text is not displayed.
 * @param {boolean} [props.isExternal] - Whether the link should be rendered as an anchor or route link.
 * @param {boolean} [props.isAtTop] - Whether the user scrolled at the top or not.
 * @param {number} [props.itemsCount] - A label to display count of items using Tag atom component.
 * @param {boolean} [props.isInSidebar] - Indicates if the header link is contained inside the sidebar.
 * @param {boolean} [props.scrollToTop=false] - Indicates if scroll to top behaviour on navigation should be enabled.
 * @param {Function} [props.onClick] - onClick event callback function.
 * @param {React.ReactNode} [props.children] - The content to display inside the link.
 *
 * @returns {React.FC<HeaderLinkProps>} The rendered `HeaderLink` component.
 */
const HeaderLink: React.FC<HeaderLinkProps> = ({
    href,
    id,
    iconName,
    iconSize = 'sm',
    colorMode = 'dark',
    shouldShowText = true,
    ariaLabel,
    isExternal = false,
    isAtTop = false,
    itemsCount,
    isInSidebar = false,
    scrollToTop = false,
    onClick,
    children,
}) => {
    const isTelephone = iconName === 'IconContact' // Telephone has a custom render for viewports
    const showText = shouldShowText || isInSidebar

    /**
     * @name isItemsCountMoreThanOneDigit
     *
     * @description
     * This utility function is used to calculate the position of `Tag` component for items count.
     *
     * @returns {boolean} - True if items count has more than one digit.
     */
    const isItemsCountMoreThanOneDigit = (): boolean => {
        if (itemsCount) {
            return Math.abs(itemsCount) >= 10
        }

        return false
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && onClick) {
            onClick()
        }
    }

    const enableMultiDigitTag = isItemsCountMoreThanOneDigit()

    const styles = useMultiStyleConfig('HeaderLink', {colorMode, isTelephone, isAtTop, isInSidebar, enableMultiDigitTag}) as Record<
        string,
        FlexProps & TextProps & LinkProps
    >

    return (
        <Flex
            as={href ? (isExternal ? Link : RouteLink) : Flex}
            {...styles.linkWrapper}
            href={href}
            to={{pathname: href, state: {scrollToTop}}}
            id={id}
            data-testid="header-link"
            role="button"
            onClick={onClick}
            onKeyUp={handleKeyDown}
            tabIndex={0}
            aria-label={ariaLabel}
        >
            {iconName && <Icon name={iconName} size={iconSize} />}

            {showText && <Text {...(styles.linkText as TextProps)}>{children}</Text>}

            {!isInSidebar && itemsCount !== undefined && itemsCount !== 0 && (
                <Flex {...styles.tagWrapper}>
                    <Tag borderRadius="full" colorScheme="dark" size="sm">
                        {itemsCount}
                    </Tag>
                </Flex>
            )}
        </Flex>
    )
}

export {HeaderLink}
